import { releaseYear, reportedQuarterStart, toQuarter } from "./date";
import { Item } from "./model";
import { ReleaseSummary, releaseSummary } from "./releases";

/**
 * Pure mapping from radar data to the JSON TimelineJS expects.
 *
 * Kept apart from the React component on purpose: TimelineJS is imperative and
 * manipulates the DOM directly, so it has to be mocked under jsdom. The mapping
 * stays unit tested regardless.
 */

export type TimelineDate = {
  year: number;
  month: number;
  day: number;
};

export type TimelineEvent = {
  /** The release date, and therefore the key of the round trip with `?release=`. */
  unique_id: string;
  start_date: TimelineDate;
  text: {
    headline: string;
    text: string;
  };
};

export type TimelineData = {
  events: TimelineEvent[];
};

/** Beyond this the slide stops being a summary. */
const MAX_LISTED_ITEMS = 14;

const nameList = (items: Item[]) => {
  const listed = items.slice(0, MAX_LISTED_ITEMS).map((item) => item.title);
  const remaining = items.length - listed.length;
  return remaining > 0
    ? `${listed.join(", ")} and ${remaining} more`
    : listed.join(", ");
};

/**
 * Headline clause for a release.
 *
 * The oldest release is an initialisation, not a wave of arrivals: every one of
 * its entries has its first revision there, so "156 new" would be true and
 * meaningless at once.
 */
export const summaryLine = ({
  initial,
  total,
  added,
  changed,
}: ReleaseSummary) => {
  if (initial) {
    return `initial release · ${total} ${
      total === 1 ? "technology" : "technologies"
    }`;
  }

  const parts = [];
  if (added.length) {
    parts.push(`${added.length} new`);
  }
  if (changed.length) {
    parts.push(
      `${changed.length} ${changed.length === 1 ? "movement" : "movements"}`
    );
  }
  return parts.length ? parts.join(" · ") : "no movement";
};

/**
 * Body of a slide, in two columns: what the quarter weighs on the left, what
 * actually moved on the right. The stylesheet lays the two `tl-radar-*` blocks
 * out side by side, next to the quarter label.
 */
const slideText = (summary: ReleaseSummary, total: number) => {
  const stats = [
    `<p class="tl-radar-summary">${summaryLine(summary)}</p>`,
    `<p class="tl-radar-total">${total} technologies on the radar at this quarter.</p>`,
  ];

  const changes = [];
  if (summary.added.length) {
    changes.push(
      `<p class="tl-radar-list"><strong>New:</strong> ${nameList(
        summary.added
      )}</p>`
    );
  }
  if (summary.changed.length) {
    changes.push(
      `<p class="tl-radar-list"><strong>Moved:</strong> ${nameList(
        summary.changed
      )}</p>`
    );
  }

  return [
    `<div class="tl-radar-columns">`,
    `<div class="tl-radar-stats">${stats.join("")}</div>`,
    changes.length
      ? `<div class="tl-radar-changes">${changes.join("")}</div>`
      : "",
    `</div>`,
  ].join("");
};

/**
 * One TimelineJS event per published release, oldest first.
 *
 * `start_date` is the first day of the quarter the release *reports on*, not of
 * the quarter it is dated in - see `toQuarter` in `date.ts`.
 */
export const toTimelineEvents = (
  items: Item[],
  releases: string[]
): TimelineData => {
  const ordered = releases.slice().sort();

  return {
    events: ordered.map((release) => {
      const summary = releaseSummary(items, release, ordered);

      return {
        unique_id: release,
        start_date: reportedQuarterStart(release),
        text: {
          headline: toQuarter(release),
          text: slideText(summary, summary.total),
        },
      };
    }),
  };
};

/** Year the release reports on, exposed for grouping and labels. */
export const eventYear = releaseYear;
